import { ContentWorkflow } from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useReducer, useState, type JSX } from 'react';
import {
  ContentWorkflowTableItem,
  ContentWorkflowV3CsvFormat,
} from '../workflow-viewer/ContentWorkflowTableDefinitions';
import { ContentAttributes, WorkflowActions } from '../../model/Enum';
import WorkflowActionControls from '../administration/WorkflowActionControls';
import PaginatedTableV3 from './PaginatedTableV3';
import {
  ContentQueryContext,
  ContentQueryContextState,
} from '../content/table/ContentQueryContext';
import { DateTime } from 'luxon';
import { TableQueryErrorInfo } from '../content/table/TableQueryErrorInfo';
import { TableProps } from '@amzn/awsui-components-core/table/interfaces';

interface ContentWorkflowTableProps {
  allowedControlActions?: WorkflowActions[];
  columnDefinitions: any[];
  defaultQuery?: any;
  header?: string;
  headerDescription?: string;
  showCustomFilters?: boolean;
  tableId: string;
  tableSelectorOptions: any[];
  tableDataProvider: JSX.Element;
  showRestorePreviousVersion?: boolean;
  onWorkflowListChange?: (wf: ContentWorkflowTableItem[]) => void;
  sortingOption?: any;
  stickyColumns?: TableProps.StickyColumns;
}

const ContentWorkflowTableV3: React.FunctionComponent<ContentWorkflowTableProps> = (
  props: ContentWorkflowTableProps,
) => {
  const {
    allowedControlActions,
    columnDefinitions,
    defaultQuery,
    header,
    headerDescription,
    showCustomFilters,
    tableId,
    tableSelectorOptions,
    showRestorePreviousVersion,
    onWorkflowListChange,
  } = props;
  const [selectedWorkflows, setSelectedWorkflows] = useState<ContentWorkflowTableItem[]>([]);
  const [queryState, dispatchQueryState] = useReducer(
    (state: ContentQueryContextState, newState: Partial<ContentQueryContextState>) => {
      return { ...state, ...newState };
    },
    { queryTimestamp: DateTime.now(), isExecutingQuery: true, queryResult: [] },
  );
  const csvFormat: Array<{ header: string; value: (i: ContentWorkflowTableItem) => any }> =
    ContentWorkflowV3CsvFormat(tableId);
  const csvFileName = 'apg-content.csv';

  useEffect(() => {
    setSelectedWorkflows([]);
    if (onWorkflowListChange) {
      // @ts-expect-error TS2345 - strict migration
      onWorkflowListChange(queryState.queryResult);
    }
  }, [queryState.queryResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = (event: any, condition?: any) => {
    dispatchQueryState({
      queryTimestamp: DateTime.now(),
      isExecutingQuery: true,
    });
  };

  const updatedItem = async (updatedAttribute?: string, updatedValue?: any) => {
    let checkingCondition;
    switch (updatedAttribute) {
      case ContentAttributes.Author:
        checkingCondition = (wfs: ContentWorkflow[]) =>
          wfs.some(
            (i) =>
              selectedWorkflows.some((swf) => swf.contentId === i.contentId) &&
              i.ownerId !== updatedValue.authorId,
          );
        break;
      default:
        break;
    }
    return handleRefresh(null, checkingCondition);
  };

  const deletedItem = async (deletedContentWorkflows: ContentWorkflow[]) => {
    const checkingCondition = (wfs: ContentWorkflow[]) =>
      wfs.some((i) => deletedContentWorkflows.some((swf) => swf.contentId === i.contentId));
    return handleRefresh(null, checkingCondition);
  };

  const postApgAction = async (wf: ContentWorkflow[]) => {
    const checkingCondition = (wfs: ContentWorkflow[]) =>
      wfs.some(
        (i) =>
          i.apgPublicationDate &&
          wf.some(
            (w) =>
              w.contentId === i.contentId &&
              w.apgPublicationDate &&
              // @ts-expect-error TS2345 - strict migration
              w.apgPublicationDate.equals(i.apgPublicationDate),
          ),
      );
    return handleRefresh(null, checkingCondition);
  };

  const actionControls =
    Array.isArray(allowedControlActions) && allowedControlActions.length ? (
      <WorkflowActionControls
        key={selectedWorkflows.map((sw) => sw.contentId)}
        workflows={selectedWorkflows.map((sw) => sw.workflow)}
        onWorkflowUpdated={updatedItem}
        onWorkflowDeleted={deletedItem}
        onWorkflowPermissionUpdate={handleRefresh}
        onWorkflowReviewersUpdate={handleRefresh}
        onWorkflowUnpublished={handleRefresh}
        onAPGPublished={postApgAction}
        onAPGUnpublished={postApgAction}
        allowedActions={allowedControlActions}
        customLayout
        showRestorePreviousVersion={!!showRestorePreviousVersion}
      />
    ) : null;

  let queryErrorInfo: JSX.Element;
  if (queryState.queryError) {
    queryErrorInfo = <TableQueryErrorInfo errorMessage={queryState.queryError} />;
  }

  return (
    <ContentQueryContext.Provider value={{ queryState, dispatchQueryState }}>
      <PaginatedTableV3
        // @ts-expect-error TS2322 - strict migration
        actionControls={actionControls}
        columnDefinitions={columnDefinitions}
        csvFormat={csvFormat}
        csvFileName={csvFileName}
        defaultQuery={defaultQuery}
        header={header}
        headerDescription={headerDescription}
        // @ts-expect-error TS2322 - strict migration
        items={queryState.queryResult}
        loading={queryState.isExecutingQuery}
        selectedItems={selectedWorkflows}
        showCustomFilters={showCustomFilters}
        onSelectionChange={setSelectedWorkflows}
        tableSelectorOptions={tableSelectorOptions}
        tableId={tableId}
        tableDataProvider={props.tableDataProvider}
        // @ts-expect-error TS2322 - strict migration
        queryTimestamp={queryState.queryTimestamp}
        // @ts-expect-error TS2454 - strict migration
        queryErrorInfo={queryErrorInfo}
        sortingOption={props.sortingOption}
        stickyColumns={props.stickyColumns}
      />
    </ContentQueryContext.Provider>
  );
};

export default ContentWorkflowTableV3;
